/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import {
    IGroup,
    IGroupDelivery,
    IGroups
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-groups';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import { IOrderConfirmationConfig, IOrderConfirmationProps } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import { IContentBlockConfig } from '../definition-extensions/content-block.ext.props.autogenerated';
import { currencyFormat, dateFormatByLocale, incExcPriceFormat, taxPercentage } from '../../../utils/helper';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core-internal';
import { IOrderConfirmationData } from './order-confirmation.data';
import MsDyn365 from '@msdyn365-commerce/core';

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation & IOrderConfirmationConfig> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
    // VSI customization for new designs implementation
    <></>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine & IOrderSummaryExtend> = ({
    salesLineProps,
    salesLine,
    buyAgainButton,
    data,
    context,
    customerTaxPercentage
}) => {
    const { enabledIncExPrices } = context?.app.config;
    const unitPrice = Number(data.salesLine.NetAmount!) / Number(data.salesLine.Quantity);
    return (
        <>
            {/* <Node {...salesLineProps}> */}
            <tr>
                <td> {data.product?.ItemId}</td>
                <td>{data.product?.Name}</td>
                <td>{incExcPriceFormat(unitPrice, context, enabledIncExPrices, customerTaxPercentage)}</td>
                <td>{data.product?.DefaultUnitOfMeasure}</td>
                <td>
                    <span className='qty'>{data.salesLine.Quantity}</span>
                </td>
                {/* <td> {salesLine}</td> */}
                <td>{incExcPriceFormat(data.salesLine.NetAmount!, context, enabledIncExPrices, customerTaxPercentage)}</td>
            </tr>
            {/* </Node> */}
        </>
    );
};

// VSI CUSTOMIZATION FOR MOBILE LIST VIEW
export const OrderConfirmationSalesLineForMobile: React.FC<ISalesLine & IOrderSummaryExtend> = ({
    salesLineProps,
    salesLine,
    buyAgainButton,
    data,
    context,
    customerTaxPercentage
}) => {
    const { enabledIncExPrices } = context?.app.config;
    return (
        <>
            <div className='order-confirmation_mobile-contianer'>
                <div className='p-no_container item'>
                    <span className='name'>Item Number: </span>
                    <span className='value'>{data.product?.ItemId}</span>
                </div>
                <div className='p-name_container item'>
                    <span className='name'>Product Name: </span>
                    <span className='value'>{data.product?.Name}</span>
                </div>
                <div className='unit-price_container item'>
                    <span className='name'>Unit Price: </span>
                    <span className='value'>
                        {incExcPriceFormat(data.product?.Price!, context, enabledIncExPrices, customerTaxPercentage)}
                    </span>
                </div>
                <div className='uom_container item'>
                    <span className='name'>UOM: </span>
                    <span className='value'>{data.product?.DefaultUnitOfMeasure}</span>
                </div>
                <div className='quan_container item'>
                    <span className='name'>Quantity: </span>
                    <span className='value'> {data.salesLine.Quantity}</span>
                </div>
                <div className='t-price_container item'>
                    <span className='name'>Total Price: </span>
                    <span className='value'>
                        {incExcPriceFormat(data.salesLine.NetAmount!, context, enabledIncExPrices, customerTaxPercentage)}
                    </span>
                </div>
            </div>
        </>
    );
};

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo,
    pickupDateTimeslot
}) => (
    // VSI customization for new designs implementation
    <></>
);

export const OrderConfirmationGroup: React.FC<IGroup & IOrderSummaryExtend> = ({
    groupProps,
    delivery,
    salesLinesProps,
    salesLines,
    context,
    customerTaxPercentage
}) => {
    //Configs For Mobile View
    const [isMobile, setIsMobile] = React.useState<boolean>();
    const _updateViewport = (): void => {
        const viewport = _getViewport();
        viewport === 'xs' || viewport === 'sm' ? setIsMobile(true) : setIsMobile(false);
    };
    type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

    const _getViewport = (): GridSize => {
        // const { context } = productManufacturarAttribute;

        // Always render in mobile viewport on a mobile device
        if (context?.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context?.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    };

    React.useEffect(() => {
        _updateViewport();
        if (MsDyn365.isBrowser && window.addEventListener) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            window.addEventListener('resize', _updateViewport);
        }
    }, []);
    if (isMobile) {
        return (
            salesLines && (
                <>
                    {salesLines.map((salesLine: ISalesLine) => (
                        <OrderConfirmationSalesLineForMobile
                            key={salesLine.data.salesLine.LineId}
                            {...salesLine}
                            context={context}
                            customerTaxPercentage={customerTaxPercentage}
                        />
                    ))}
                </>
            )
        );
    } else {
        return (
            <Node {...groupProps}>
                {/* {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
                {delivery && delivery.shippingItemsToYou} */}

                <table>
                    <tr>
                        <th>Item Number</th>
                        <th>Product Name</th>
                        <th>Unit Price</th>
                        <th>UOM</th>
                        <th>
                            <span className='qty'>Quantity</span>
                        </th>
                        <th>Total Price</th>
                    </tr>
                    {salesLines && (
                        <>
                            {salesLines.map((salesLine: ISalesLine) => (
                                <OrderConfirmationSalesLine
                                    key={salesLine.data.salesLine.LineId}
                                    {...salesLine}
                                    context={context}
                                    customerTaxPercentage={customerTaxPercentage}
                                />
                            ))}
                        </>
                    )}
                </table>
            </Node>
        );
    }
};

export const OrderConfirmationGroups: React.FC<IGroups & IOrderSummaryExtend> = ({
    groupsProps,
    groups,
    context,
    customerTaxPercentage
}) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup key={index} {...group} context={context} customerTaxPercentage={customerTaxPercentage} />
        ))}
    </Node>
);
interface IOrderSummaryExtend {
    discountAmount?: number;
    context?: ICoreContext<IGeneric<IAny>>;
    deliveryMode?: string;
    freightAndGSTMessage?: string;
    freightAndGSTMessageFromResources?: string;
    customerTaxPercentage?: number;
}
interface JSXNumberValue {
    value: number;
}

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary & IOrderSummaryExtend> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    discountAmount,
    deliveryMode,
    context,
    freightAndGSTMessage,
    freightAndGSTMessageFromResources
}) => {
    const subTotalJSX = subtotal as JSX.Element;
    const subTotalJSXValue = subTotalJSX.props as JSXNumberValue;
    const subTotalAmount = subTotalJSXValue?.value;
    const taxProps = tax as JSX.Element;
    const taxAmount = taxProps.props.value;
    const totalProps = totalAmount as JSX.Element;
    const totalAmountValue = totalProps.props.value;
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {/* {subtotal} */}
            <p className='ms-order-confirmation__order-summary-line-subtotal'>
                <span className='ms-order-confirmation__order-summary-label'>Subtotal</span>
                <span className='msc-price ms-order-confirmation__order-summary-price'>
                    <span className='msc-price__actual'>{currencyFormat(subTotalAmount, context)}</span>
                </span>
            </p>
            <p className='ms-order-confirmation__order-summary-line-shipping'>
                <span className='ms-order-confirmation__order-summary-label'>Shipping Fee</span>
                <span className='msc-price ms-order-confirmation__order-summary-price'>
                    {deliveryMode && deliveryMode === 'Collection' ? (
                        <span className='msc-price__actual' itemProp='price'>
                            FREE
                        </span>
                    ) : (
                        <div className='to-be-calculated'>
                            <span className='msc-order-summary__value'>{'To Be Calculated '}</span>
                            <div className='tooltip-wrapper' tabIndex={0}>
                                <span className='tooltip-icon' />
                                <span className='tooltip-text tooltip-left'>
                                    {
                                        'This quotation is excluding freight costs. Freight will be detailed in your sales order confirmation.'
                                    }
                                </span>
                            </div>
                        </div>
                    )}
                </span>
            </p>
            <p>
                <span className='ms-order-confirmation__order-summary-label'>Tax</span>
                <span className='msc-price ms-order-confirmation__order-summary-price'>
                    <span className='msc-price__actual' itemProp='price'>
                        {currencyFormat(taxAmount, context)}
                    </span>
                </span>
            </p>
            <p className='ms-order-confirmation__order-summary-line-total-amount'>
                <span className='ms-order-confirmation__order-summary-label'>Total</span>
                <span className='msc-price ms-order-confirmation__order-summary-price'>
                    <span className='msc-price__actual' itemProp='price'>
                        {currencyFormat(totalAmountValue, context)}
                    </span>
                </span>
            </p>
            <div className='checkout-summary-freight'>
                <span>{freightAndGSTMessage ? freightAndGSTMessage : freightAndGSTMessageFromResources}</span>
            </div>
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {/* // VSI customization for new designs implementation */}

        {/* {title} */}
        {/* {methods} */}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationData>> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    data,
    resources,
    config: { bannerImage, bannerLink, freightAndGSTMessage },
    context: {
        request: { gridSettings },
        actionContext: { requestContext }
    },
    context
}) => {
    const deliveryMode = data?.orderHydration?.result?.salesOrder?.DeliveryMode!;
    //VSI CUSTOMIZATION TO SET CHECKOUT PHONE NUMBER
    const [phnRcdId, setPhnRcdId] = React.useState<Number | undefined>();
    const [phnRcdNumber, setPhnRcdNumber] = React.useState<string>();

    React.useEffect(() => {
        setTimeout(() => {
            const phnRcdId = localStorage.getItem('phnRcdId');
            setPhnRcdId(Number(phnRcdId!));
        }, 1000);
    }, []);

    React.useEffect(() => {
        if (phnRcdId) {
            const primaryAddresses = data.customerInformation.result?.Addresses?.filter(e => e.PhoneRecordId === phnRcdId);
            if (primaryAddresses) setPhnRcdNumber(primaryAddresses[0]?.Phone!);
        }
    }, [phnRcdId]);

    //VSI Customization to check if phone number exists
    const discountAmount = data?.orderHydration.result?.salesOrder?.DiscountAmount || undefined;
    const addressProps = groups && groups?.groups?.find(addr => addr?.delivery?.address)?.delivery.address;

    //VSI Customization to add banner image from config for new design implementation
    const imageProps = {
        gridSettings: gridSettings ?? {},
        imageSettings: bannerImage?.imageSettings
    };

    interface IContentBlockFullConfig extends IContentBlockConfig {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- This field comes from SDK.
        msdyn365__moduleLayout?: string;
    }
    const contentBlockImage = (
        <Msdyn365.Image
            {...bannerImage}
            {...imageProps}
            requestContext={requestContext}
            editProps={{
                key: bannerImage ?? {},
                requestContext: requestContext,
                // moduleType: typeName,
                imagePropertyName: 'image',
                // moduleId: props.id,
                layout: (bannerImage as IContentBlockConfig) && (bannerImage as IContentBlockFullConfig).msdyn365__moduleLayout
            }}
            shouldSkipToMainImage
        />
    );
    //VSI Customization to add banner image for new design implementation -- END
    const newDate = new Date(data?.orderHydration.result?.salesOrder.CreatedDateTime!);
    const formattedDate = newDate.toLocaleDateString('en-GB');
    /* VSI CUSTOMIZATION | DATE FORMAT | START */
    const dateLocale = context.app.config.dateFormat;
    const currency = context.request.channel?.Currency;
    const formattedDateByLocale: string =
        (dateLocale && currency && formattedDate && dateFormatByLocale(dateLocale, currency, formattedDate)) || '';
    const customerTaxPercentage = taxPercentage(data.accountInformation?.result!);
    /* VSI CUSTOMIZATION | DATE FORMAT | END */
    return (
        <>
            <Module {...moduleProps}>
                <div className='heading-backtoshop'>
                    {heading}
                    {backToShoppingLink}
                </div>
                {/* //VSI Customization to change shipping address & order details layout for new design implementation */}
                {
                    <div className='address-order-request-section'>
                        <div className='Shipping-address-section'>
                            <h2 className='shipping-address-heading'>{resources.shippingAddressyHeading}</h2>
                            {/* <span className='customer-name'>{data?.orderHydration?.result?.salesOrder?.CustomerName}</span> */}
                            <span className='Address-name'>{addressProps && addressProps}</span>
                            {/* //VSI Customization to check if phone number exists */}

                            {/* {data?.customerInformation?.result?.Phone && (
                                <div className='order-confirmation-phone-container'>
                                    <span className='_label'>{resources.phoneNumberLabel}</span>
                                    <span className='_value'>{data?.customerInformation?.result?.Phone}</span>
                                </div>
                            )} */}
                            <div className='order-confirmation-phone-container'>
                                <span className='_label'>{resources.phoneNumberLabel}</span>
                                <span className='_value'>{phnRcdNumber}</span>
                            </div>
                        </div>
                        <div className='order-request-details-section'>
                            <h2 className='order-request-details-heading'>{resources.orderRequestDetailsHeading}</h2>
                            <div>
                                <label>{resources.orderRequestIDLabel}</label>
                                <span className='order-request-details-ID'>
                                    {data?.orderHydration?.result?.salesOrder?.ChannelReferenceId}
                                </span>
                            </div>
                            <div>
                                <label>{resources.orderRequestEmailLabel}</label>
                                <span className='order-request-details-email'>
                                    {data?.orderHydration?.result?.salesOrder?.ReceiptEmail}
                                </span>
                            </div>
                            <div className='order-request-details-date'>
                                {resources.orderRequestDateLabel.replace('{date}', formattedDateByLocale!)}
                            </div>
                        </div>
                    </div>
                }
                {/* //VSI Customization to change shipping address & order details layout for new design implementation -- END */}
                {/* {data.orderHydration.result?.salesOrder?.ShippingAddress?.Name}
                {data.orderHydration.result?.salesOrder?.ShippingAddress?.FullAddress} */}
                {groups && <OrderConfirmationGroups {...groups} context={context} customerTaxPercentage={customerTaxPercentage} />}
                {alert}
                {loading}

                {/* {payment && <OrderConfirmationPayment {...payment} />} */}
                <div className='summary-container'>
                    {bannerLink && bannerLink.linkUrl && bannerLink.linkUrl.destinationUrl && (
                        <div className='order-details-bannerImage'>
                            <a
                                href={bannerLink?.linkUrl?.destinationUrl!}
                                target={bannerLink.openInNewTab ? '_blank' : ''}
                                aria-label={bannerLink?.ariaLabel!}
                            >
                                {contentBlockImage}
                            </a>
                        </div>
                    )}
                    <div className='summary-wrapper'>
                        <h2>{resources.paymentSummaryText}</h2>
                        {orderSummary && (
                            <OrderConfirmationOrderSummary
                                {...orderSummary}
                                discountAmount={discountAmount}
                                context={context}
                                deliveryMode={deliveryMode}
                                freightAndGSTMessage={freightAndGSTMessage}
                                freightAndGSTMessageFromResources={resources.freightAndGSTMessageResources}
                            />
                        )}
                    </div>
                </div>
                {help && <OrderConfirmationHelp {...help} />}
            </Module>
        </>
    );
};

export default OrderConfirmationView;
